import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useContent, photoUrl } from "../context/ContentContext";
import { useLang } from "../context/LanguageContext";
import { translations, LANGUAGES } from "../i18n/translations";
import { AMENITIES } from "../data/content";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Upload, Trash2, Star, Loader2, Save, RefreshCw, Copy, Plus, Link2 } from "lucide-react";

const LANGS = ["it", "en", "de", "fr", "es"];
const CATS = ["interior", "rooms", "kitchen", "outdoor"];

const langLabel = (c) => LANGUAGES.find((l) => l.code === c)?.label || c;

/* -------- Photos -------- */
export const PhotosPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [cat, setCat] = useState("interior");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/content/photos?cat=${cat}`, fd);
      await reload();
      toast.success(t("owner.saved"));
    } catch (err) {
      toast.error("Upload error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/content/photos/${id}`);
    await reload();
    toast.success(t("owner.saved"));
  };

  const setCover = async (id) => {
    await api.put("/content", { hero_photo_id: id });
    await reload();
    toast.success(t("owner.coverSet"));
  };

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-photos">
      <p className="mb-2 text-sm text-stone-500">{t("owner.photosHint")}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.category")}</Label>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-44" data-testid="photo-cat-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATS.map((c) => (
                <SelectItem key={c} value={c}>{t(`gallery.filters.${c}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="photo-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="photo-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("owner.uploadPhoto")}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {(content.photos || []).map((p) => (
          <div key={p.id} className="group relative overflow-hidden rounded-xl border border-stone-200" data-testid={`photo-item-${p.id}`}>
            <img src={photoUrl(p.id)} alt="" className="h-36 w-full object-cover" />
            {content.hero_photo_id === p.id && (
              <span className="absolute left-2 top-2 rounded-full bg-[#C85A32] px-2 py-0.5 text-xs font-semibold text-white">★ {t("owner.cover")}</span>
            )}
            <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">{t(`gallery.filters.${p.cat}`)}</span>
            <div className="absolute right-2 top-2 flex flex-col gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button onClick={() => setCover(p.id)} title={t("owner.setCover")} className="rounded-full bg-white p-1.5 text-stone-700 shadow hover:text-[#C85A32]" data-testid={`photo-cover-${p.id}`}>
                <Star className="h-4 w-4" />
              </button>
              <button onClick={() => remove(p.id)} title="Delete" className="rounded-full bg-white p-1.5 text-red-600 shadow hover:bg-red-50" data-testid={`photo-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {(!content.photos || content.photos.length === 0) && (
          <p className="col-span-full py-8 text-center text-sm text-stone-400">{t("owner.photosEmpty")}</p>
        )}
      </div>
    </div>
  );
};

/* -------- Texts -------- */
export const TextsPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [hero, setHero] = useState(() => {
    const title = {}, subtitle = {};
    LANGS.forEach((l) => {
      title[l] = content.hero?.title?.[l] ?? translations[l].hero.title;
      subtitle[l] = content.hero?.subtitle?.[l] ?? translations[l].hero.subtitle;
    });
    return { title, subtitle };
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { hero });
      await reload();
      toast.success(t("owner.saved"));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-texts">
      <p className="mb-4 text-sm text-stone-500">{t("owner.textsHint")}</p>
      <div className="space-y-6">
        {LANGS.map((l) => (
          <div key={l} className="rounded-xl border border-stone-100 p-4">
            <p className="mb-3 text-sm font-semibold text-stone-700">{langLabel(l)}</p>
            <Label className="mb-1.5 block text-xs">{t("owner.heroTitle")}</Label>
            <Input value={hero.title[l]} onChange={(e) => setHero((h) => ({ ...h, title: { ...h.title, [l]: e.target.value } }))} data-testid={`text-title-${l}`} />
            <Label className="mb-1.5 mt-3 block text-xs">{t("owner.heroSubtitle")}</Label>
            <Textarea rows={2} value={hero.subtitle[l]} onChange={(e) => setHero((h) => ({ ...h, subtitle: { ...h.subtitle, [l]: e.target.value } }))} data-testid={`text-subtitle-${l}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="texts-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("owner.save")}
      </Button>
    </div>
  );
};

/* -------- Pricing & rules -------- */
export const PricingPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [pricing, setPricing] = useState({
    nightlyRate: content.pricing?.nightlyRate ?? 100,
    cleaningFee: content.pricing?.cleaningFee ?? 30,
    maxGuests: content.pricing?.maxGuests ?? 4,
  });
  const [checkin, setCheckin] = useState(() => {
    const o = {}; LANGS.forEach((l) => { o[l] = content.checkin?.[l] ?? translations[l].pricing.rules.checkin; }); return o;
  });
  const [checkout, setCheckout] = useState(() => {
    const o = {}; LANGS.forEach((l) => { o[l] = content.checkout?.[l] ?? translations[l].pricing.rules.checkout; }); return o;
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", {
        pricing: { nightlyRate: Number(pricing.nightlyRate), cleaningFee: Number(pricing.cleaningFee), maxGuests: Number(pricing.maxGuests) },
        checkin, checkout,
      });
      await reload();
      toast.success(t("owner.saved"));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-pricing">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.nightlyRate")} (€)</Label>
          <Input type="number" value={pricing.nightlyRate} onChange={(e) => setPricing({ ...pricing, nightlyRate: e.target.value })} data-testid="price-nightly" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.cleaningFee")} (€)</Label>
          <Input type="number" value={pricing.cleaningFee} onChange={(e) => setPricing({ ...pricing, cleaningFee: e.target.value })} data-testid="price-cleaning" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.maxGuests")}</Label>
          <Input type="number" value={pricing.maxGuests} onChange={(e) => setPricing({ ...pricing, maxGuests: e.target.value })} data-testid="price-guests" />
        </div>
      </div>
      <div className="mt-6 space-y-3">
        <p className="text-sm font-semibold text-stone-700">{t("owner.times")}</p>
        {LANGS.map((l) => (
          <div key={l} className="grid grid-cols-1 gap-2 sm:grid-cols-[80px_1fr_1fr] sm:items-center">
            <span className="text-sm text-stone-500">{langLabel(l)}</span>
            <Input value={checkin[l]} onChange={(e) => setCheckin({ ...checkin, [l]: e.target.value })} placeholder="Check-in" data-testid={`checkin-${l}`} />
            <Input value={checkout[l]} onChange={(e) => setCheckout({ ...checkout, [l]: e.target.value })} placeholder="Check-out" data-testid={`checkout-${l}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="pricing-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("owner.save")}
      </Button>
    </div>
  );
};

/* -------- Amenities -------- */
export const AmenitiesPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [items, setItems] = useState(() => {
    const existing = content.amenities && content.amenities.length
      ? Object.fromEntries(content.amenities.map((a) => [a.key, a.enabled !== false]))
      : {};
    return AMENITIES.map((a) => ({ key: a.key, enabled: existing[a.key] ?? true }));
  });
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setItems((arr) => arr.map((a) => (a.key === key ? { ...a, enabled: !a.enabled } : a)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { amenities: items });
      await reload();
      toast.success(t("owner.saved"));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-amenities">
      <p className="mb-4 text-sm text-stone-500">{t("owner.amenitiesHint")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((a) => (
          <div key={a.key} className="flex items-center justify-between rounded-xl border border-stone-100 p-3.5">
            <span className="text-sm font-medium text-stone-800">{t(`amenities.items.${a.key}`)}</span>
            <Switch checked={a.enabled} onCheckedChange={() => toggle(a.key)} data-testid={`amenity-toggle-${a.key}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="amenities-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("owner.save")}
      </Button>
    </div>
  );
};


/* -------- iCal sync -------- */
export const SyncPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [ical, setIcal] = useState({
    airbnb: content.ical?.airbnb ?? "",
    booking: content.ical?.booking ?? "",
  });
  const [syncing, setSyncing] = useState(false);
  const exportUrl = `${API}/ical/export.ics`;

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.put("/content", { ical: { airbnb: ical.airbnb.trim(), booking: ical.booking.trim() } });
      const { data } = await api.post("/ical/sync");
      await reload();
      const parts = Object.entries(data.results || {}).map(([p, r]) =>
        r.ok ? `${p}: ${r.count}` : `${p}: ✕`);
      toast.success(`${t("owner.saved")} · ${data.blocked_count} ${parts.length ? "(" + parts.join(", ") + ")" : ""}`);
    } catch (e) {
      toast.error("Sync error");
    } finally {
      setSyncing(false);
    }
  };

  const copyExport = () => {
    navigator.clipboard?.writeText(exportUrl);
    toast.success(t("owner.icalCopied"));
  };

  const lastSync = content.ical_last_sync
    ? new Date(content.ical_last_sync).toLocaleString()
    : t("owner.icalNever");
  const results = content.ical_sync_result || {};

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-sync">
      <p className="mb-4 text-sm text-stone-500">{t("owner.syncHint")}</p>

      <div className="space-y-4">
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.icalAirbnb")}</Label>
          <Input value={ical.airbnb} onChange={(e) => setIcal({ ...ical, airbnb: e.target.value })}
            placeholder="https://www.airbnb.com/calendar/ical/..." data-testid="ical-airbnb-input" />
          {results.airbnb && (
            <p className={`mt-1 text-xs ${results.airbnb.ok ? "text-emerald-600" : "text-red-600"}`}>
              {results.airbnb.ok ? `✓ ${results.airbnb.count}` : `✕ ${results.airbnb.error}`}
            </p>
          )}
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">{t("owner.icalBooking")}</Label>
          <Input value={ical.booking} onChange={(e) => setIcal({ ...ical, booking: e.target.value })}
            placeholder="https://admin.booking.com/hotel/.../ical..." data-testid="ical-booking-input" />
          {results.booking && (
            <p className={`mt-1 text-xs ${results.booking.ok ? "text-emerald-600" : "text-red-600"}`}>
              {results.booking.ok ? `✓ ${results.booking.count}` : `✕ ${results.booking.error}`}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={syncNow} disabled={syncing} className="gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="ical-sync-btn">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {syncing ? t("owner.icalSyncing") : t("owner.icalSyncNow")}
        </Button>
        <span className="text-xs text-stone-500">{t("owner.icalLastSync")}: {lastSync}</span>
      </div>

      <div className="mt-6 rounded-xl border border-stone-100 bg-stone-50 p-4">
        <Label className="mb-1.5 flex items-center gap-1.5 text-xs"><Link2 className="h-3.5 w-3.5" /> {t("owner.icalExportLabel")}</Label>
        <p className="mb-2 text-xs text-stone-500">{t("owner.icalExportHint")}</p>
        <div className="flex gap-2">
          <Input readOnly value={exportUrl} className="bg-white text-xs" data-testid="ical-export-url" />
          <Button variant="outline" onClick={copyExport} className="shrink-0 gap-1.5" data-testid="ical-copy-btn">
            <Copy className="h-4 w-4" /> {t("owner.icalCopy")}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* -------- Seasonal pricing -------- */
export const SeasonsPanel = () => {
  const { content, reload } = useContent();
  const { t } = useLang();
  const [seasons, setSeasons] = useState(() =>
    (content.seasons || []).map((s) => ({ label: s.label || "", start: s.start || "", end: s.end || "", rate: s.rate ?? "" }))
  );
  const [saving, setSaving] = useState(false);

  const add = () => setSeasons((arr) => [...arr, { label: "", start: "", end: "", rate: "" }]);
  const remove = (i) => setSeasons((arr) => arr.filter((_, idx) => idx !== i));
  const update = (i, key, val) => setSeasons((arr) => arr.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));

  const save = async () => {
    setSaving(true);
    try {
      const clean = seasons
        .filter((s) => s.start && s.end && s.rate !== "")
        .map((s) => ({ label: s.label, start: s.start, end: s.end, rate: Number(s.rate) }));
      await api.put("/content", { seasons: clean });
      await reload();
      toast.success(t("owner.saved"));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-seasons">
      <p className="mb-4 text-sm text-stone-500">{t("owner.seasonsHint")}</p>

      <div className="space-y-3">
        {seasons.length === 0 && (
          <p className="py-6 text-center text-sm text-stone-400" data-testid="seasons-empty">{t("owner.seasonsEmpty")}</p>
        )}
        {seasons.map((s, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border border-stone-100 p-3 sm:grid-cols-[1.4fr_1fr_1fr_0.8fr_auto] sm:items-end" data-testid={`season-row-${i}`}>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.seasonLabel")}</Label>
              <Input value={s.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Alta stagione" data-testid={`season-label-${i}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.seasonStart")}</Label>
              <Input type="date" value={s.start} onChange={(e) => update(i, "start", e.target.value)} data-testid={`season-start-${i}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.seasonEnd")}</Label>
              <Input type="date" value={s.end} onChange={(e) => update(i, "end", e.target.value)} data-testid={`season-end-${i}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.seasonRate")}</Label>
              <Input type="number" value={s.rate} onChange={(e) => update(i, "rate", e.target.value)} data-testid={`season-rate-${i}`} />
            </div>
            <Button variant="outline" size="icon" onClick={() => remove(i)} className="h-10 w-10 text-red-600 hover:bg-red-50" data-testid={`season-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button variant="outline" onClick={add} className="gap-2" data-testid="season-add-btn">
          <Plus className="h-4 w-4" /> {t("owner.addSeason")}
        </Button>
        <Button onClick={save} disabled={saving} className="gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="seasons-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("owner.save")}
        </Button>
      </div>
    </div>
  );
};

/* -------- Promotions & discount codes -------- */
export const PromosPanel = () => {
  const { t } = useLang();
  const [promos, setPromos] = useState([]);
  const [lm, setLm] = useState({ enabled: false, daysBefore: 7, percent: 10 });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/promos").then(({ data }) => {
      setPromos((data.promos || []).map((p) => ({ code: p.code || "", type: p.type || "percent", value: p.value ?? "", active: p.active !== false })));
      if (data.lastminute && Object.keys(data.lastminute).length) {
        setLm({ enabled: !!data.lastminute.enabled, daysBefore: data.lastminute.daysBefore ?? 7, percent: data.lastminute.percent ?? 10 });
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const add = () => setPromos((arr) => [...arr, { code: "", type: "percent", value: "", active: true }]);
  const remove = (i) => setPromos((arr) => arr.filter((_, idx) => idx !== i));
  const update = (i, key, val) => setPromos((arr) => arr.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const cleanPromos = promos
        .filter((p) => p.code.trim() && p.value !== "")
        .map((p) => ({ code: p.code.trim().toUpperCase(), type: p.type, value: Number(p.value), active: p.active }));
      const cleanLm = { enabled: lm.enabled, daysBefore: Number(lm.daysBefore) || 0, percent: Number(lm.percent) || 0 };
      await api.put("/content", { promos: cleanPromos, lastminute: cleanLm });
      toast.success(t("owner.saved"));
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="rounded-2xl border border-stone-200/80 bg-white p-6"><Loader2 className="h-5 w-5 animate-spin text-[#C85A32]" /></div>;

  return (
    <div className="rounded-2xl border border-stone-200/80 bg-white p-6" data-testid="panel-promos">
      <p className="mb-4 text-sm text-stone-500">{t("owner.promosHint")}</p>

      <div className="space-y-3">
        {promos.length === 0 && (
          <p className="py-4 text-center text-sm text-stone-400" data-testid="promos-empty">{t("owner.promosEmpty")}</p>
        )}
        {promos.map((p, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border border-stone-100 p-3 sm:grid-cols-[1.2fr_1.3fr_0.9fr_auto_auto] sm:items-end" data-testid={`promo-row-${i}`}>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.promoCode")}</Label>
              <Input value={p.code} onChange={(e) => update(i, "code", e.target.value.toUpperCase())} placeholder="ESTATE10" className="uppercase" data-testid={`promo-code-${i}`} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.promoType")}</Label>
              <Select value={p.type} onValueChange={(v) => update(i, "type", v)}>
                <SelectTrigger data-testid={`promo-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">{t("owner.promoPercent")}</SelectItem>
                  <SelectItem value="fixed">{t("owner.promoFixed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.promoValue")}</Label>
              <Input type="number" value={p.value} onChange={(e) => update(i, "value", e.target.value)} data-testid={`promo-value-${i}`} />
            </div>
            <div className="flex flex-col items-center">
              <Label className="mb-1 block text-xs">{t("owner.promoActive")}</Label>
              <Switch checked={p.active} onCheckedChange={(v) => update(i, "active", v)} data-testid={`promo-active-${i}`} />
            </div>
            <Button variant="outline" size="icon" onClick={() => remove(i)} className="h-10 w-10 text-red-600 hover:bg-red-50" data-testid={`promo-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button variant="outline" onClick={add} className="mt-3 gap-2" data-testid="promo-add-btn">
        <Plus className="h-4 w-4" /> {t("owner.addPromo")}
      </Button>

      <div className="mt-6 rounded-xl border border-stone-100 bg-stone-50 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-stone-700">{t("owner.lastMinuteTitle")}</p>
          <Switch checked={lm.enabled} onCheckedChange={(v) => setLm({ ...lm, enabled: v })} data-testid="lastminute-enable" />
        </div>
        {lm.enabled && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="mb-1 block text-xs">{t("owner.lastMinuteDays")}</Label>
              <Input type="number" value={lm.daysBefore} onChange={(e) => setLm({ ...lm, daysBefore: e.target.value })} data-testid="lastminute-days" />
            </div>
            <div>
              <Label className="mb-1 block text-xs">{t("owner.lastMinutePercent")}</Label>
              <Input type="number" value={lm.percent} onChange={(e) => setLm({ ...lm, percent: e.target.value })} data-testid="lastminute-percent" />
            </div>
          </div>
        )}
      </div>

      <Button onClick={save} disabled={saving} className="mt-5 gap-2 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="promos-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("owner.save")}
      </Button>
    </div>
  );
};

